import * as React from "react"

// Use Tailwind's standard 'md' breakpoint for chat layout switching
// This matches CSS classes like 'hidden md:block' and 'block md:hidden'  
const MOBILE_BREAKPOINT = 768; // Tailwind 'md' breakpoint

export interface UseIsMobileResult {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

/**
 * Optimized mobile detection hook using native MediaQuery API
 * 
 * Performance characteristics:
 * - Single MediaQuery listener (vs multiple resize listeners)
 * - Browser-native breakpoint detection
 * - Matches Tailwind CSS 'md' breakpoint (768px)
 * - SSR-safe initialization
 * 
 * Usage:
 * const { isMobile } = useIsMobile();
 * 
 * CSS equivalent:
 * - Mobile: 'block md:hidden' (< 768px)
 * - Desktop: 'hidden md:block' (≥ 768px)
 */
export function useIsMobile(): UseIsMobileResult {
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false; // SSR fallback
    return window.innerWidth < MOBILE_BREAKPOINT;
  });

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    
    const handleChange = (e: MediaQueryListEvent) => {
          setIsMobile(e.matches);
    };

    mql.addEventListener("change", handleChange);
    setIsMobile(mql.matches); // Set initial state
    
    return () => {
      mql.removeEventListener("change", handleChange);
    };
  }, []);

  // For backwards compatibility - simplified logic
  const isTablet = false; // We only distinguish mobile vs non-mobile
  const isDesktop = !isMobile;

  return { 
    isMobile, 
    isTablet, 
    isDesktop 
  };
}
